/**
 * useAgentenSystem — Hook für die Agenten-Kommandozentrale
 * 
 * LIVE-VERSION V4.1 — Kein Flackern, stabile Dependencies
 * 
 * Alle Daten kommen aus Supabase:
 * - agenten_konfiguration → Agenten-Profile & Briefings
 * - agenten_vorschlaege → Vorschläge (Ja/Nein/Verbesserung)
 * - agenten_lernregeln → Lernregeln (Muster → Aktion)
 * - agenten_protokoll → Aktivitäts-Log
 * - ki_briefing → Legacy-Vorschläge (Fallback)
 * 
 * FIX: Kein Re-Render-Loop mehr durch stabile useRef + einmaliges Laden
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

// ─── Typen ──────────────────────────────────────────────────────────────────

export type Entscheidung = 'ja' | 'nein' | 'verbesserung' | null;
export type VorschlagTyp = 'email' | 'stellungnahme' | 'aufgabe' | 'ablage' | 'ueberweisung' | 'antwort' | 'frist' | 'kalender' | 'projekt' | 'allgemein' | 'manuell' | 'anweisung_global' | 'feedback' | 'github_issue' | 'dev_review' | 'issue';

export interface AufgabenDetails {
  titel: string;
  beschreibung?: string;
  prioritaet: 'niedrig' | 'mittel' | 'hoch' | 'dringend';
  faellig_am?: string; // 'YYYY-MM-DD'
  zugewiesen_an?: string; // UUID
  business_unit: 'gutachten' | 'autoglas' | 'mietwagen' | 'werkstatt';
  akte_id?: string;
}

export interface IssueDetails {
  titel: string;
  kurzbeschreibung?: string; // Kurze Zusammenfassung für nicht-technische Nutzer
  beschreibung: string;
  labels: string[];
  prioritaet: 'niedrig' | 'mittel' | 'hoch';
  fehler_log?: string;
}

export type VorschlagStatus = 'pending' | 'approved' | 'rejected' | 'improved';
export type TabId = 'vorschlaege' | 'protokoll' | 'agenten' | 'regeln' | 'roadmap' | 'status' | 'einstellungen' | 'chat' | 'no-match-queue';

export interface AgentenVorschlag {
  id: string;
  datum: string;
  bereich: string;
  typ: 'meldung' | 'aufgabe' | 'rueckfrage' | 'fortschritt' | 'vorschlag';
  vorschlag_typ: VorschlagTyp | null;
  titel: string;
  inhalt: string;
  vorschlag_text: string | null;
  entscheidung: Entscheidung;
  verbesserung_text: string | null;
  antwort: string | null;
  antwort_datum: string | null;
  gelesen: boolean;
  erstellt_am: string;
  erstellt_von: string;
  meta: Record<string, any> | null;
  aufgaben_details: AufgabenDetails | null;
  issue_details: IssueDetails | null;
}

export interface LernRegel {
  id: string;
  kategorie: string;
  muster: string;
  aktion: string;
  beispiel: string | null;
  aktiv: boolean;
  anwendungen: number;
  erstellt_am: string;
  erstellt_von: 'admin' | 'ki_agent' | 'system';
  agent_id?: string | null;
  konfidenz?: number;
}

export interface ProtokollEintrag {
  id: string;
  agent_id: string;
  agent_name: string;
  aktion: string;
  beschreibung: string;
  kategorie: string;
  details: Record<string, any> | null;
  erfolgreich: boolean;
  fehler_meldung: string | null;
  erstellt_am: string;
}

export interface AgentBriefing {
  id: string;
  agent_id: string;
  agent_name: string;
  agent_nummer: string;
  beschreibung: string;
  briefing_inhalt: string;
  letzte_ausfuehrung: string | null;
  naechste_ausfuehrung: string | null;
  intervall: string;
  ist_aktiv: boolean;
  icon: string;
  farbe: string;
  bereiche: string[];
  darf_emails_senden: boolean;
  darf_dokumente_ablegen: boolean;
  darf_aufgaben_erstellen: boolean;
  darf_zahlungen_vorschlagen: boolean;
  managed_agent_id: string | null;
}

export interface Mitarbeiter {
  id: string;
  vorname: string;
  nachname: string;
}

export interface FeedbackEintrag {
  id: string;
  agent_id: string;
  vorschlag_id: string | null;
  feedback_typ: string; // 'angenommen' | 'abgelehnt' | 'verbessert' | 'aktion' | 'anweisung'
  original_vorschlag: string | null;
  verbesserter_text: string | null;
  user_anweisung: string | null;
  ziel_agent_id: string | null;
  gewaehlte_aktion: string | null;
  geplante_zeit: string | null;
  erstellt_am: string;
}

export interface KontextEintrag {
  id: string;
  typ: 'feedback' | 'protokoll';
  titel: string;
  beschreibung: string;
  datum: string;
  icon_typ: string; // feedback_typ oder aktion
  details?: Record<string, any>;
}

export interface BereichStats {
  bereich: string;
  offeneAufgaben: number;
  pendingVorschlaege: number;
}

// ─── Briefing-Bereiche ─────────────────────────────────────────────────────

export const BEREICHE = [
  { id: 'sv_buero', label: 'SV Büro Koop', firma: 'Gutachten & Bewertung', icon: 'FileSearch', color: '#1E3A5F' },
  { id: 'lcs', label: 'LCS / Unfallgiganten', firma: 'Mietwagen, Autoglas, Werkstatt', icon: 'Car', color: '#065F46' },
  { id: 'lenneglas', label: 'Lenneglas', firma: 'Autoglas-Marke', icon: 'Diamond', color: '#0D9488' },
  { id: 'protex', label: 'Protex / Amazon', firma: 'Produktverkauf', icon: 'Package', color: '#7C3AED' },
  { id: 'immobilien', label: 'Immobilien', firma: 'Airbnb & Vermietung', icon: 'Building', color: '#B45309' },
  { id: 'privat', label: 'Privat', firma: 'Persönliches & PROLEA', icon: 'User', color: '#6B7280' },
];

// ─── Agent-Nummern-Mapping (für UI-Sortierung) ─────────────────────────────

const AGENT_NUMMERN: Record<string, string> = {
  tagesmanager: '01',
  'sv-buero': '02',
  buchhaltung: '03',
  'email-agent': '04',
  'dokument-agent': '05',
  'finanz-agent': '06',
  'crm-agent': '07',
  'werkstatt-agent': '08',
  'marketing-agent': '09',
};

// ─── Stabile Konstanten (außerhalb der Komponente) ─────────────────────────

const HEUTE = new Date().toISOString().split('T')[0];

// ─── Helpers ───────────────────────────────────────────────────────────────

function computeGitHubLabels(details: IssueDetails): string[] {
  const labels: string[] = ['agent:claude-code'];

  const prioMap: Record<string, string> = {
    hoch: 'priority:high',
    mittel: 'priority:medium',
    niedrig: 'priority:low',
  };
  if (prioMap[details.prioritaet]) {
    labels.push(prioMap[details.prioritaet]);
  }

  if (details.labels.length > 0) {
    const first = details.labels[0].toLowerCase();
    if (first.includes('bug')) {
      labels.push('type:bug');
    } else if (first.includes('feature') || first.includes('verbesserung')) {
      labels.push('type:feature');
    } else {
      labels.push('type:refactor');
    }
    // Pass through remaining labels as-is
    labels.push(...details.labels.slice(1));
  }

  return labels;
}

// ─── Hook ──────────────────────────────────────────────────────────────────

export function useAgentenSystem() {
  const { isAdmin } = useAuth();
  const [vorschlaege, setVorschlaege] = useState<AgentenVorschlag[]>([]);
  const [lernregeln, setLernregeln] = useState<LernRegel[]>([]);
  const [protokoll, setProtokoll] = useState<ProtokollEintrag[]>([]);
  const [agenten, setAgenten] = useState<AgentBriefing[]>([]);
  const [mitarbeiter, setMitarbeiter] = useState<Mitarbeiter[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('vorschlaege');

  // ═══ ANTI-FLICKER: Refs für stabile Daten ═══════════════════════════════
  const agentenRef = useRef<AgentBriefing[]>([]);
  const hasLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);

  // ══════════════════════════════════════════════════════════════════════════
  // EINMALIGES LADEN — Kein Re-Render-Loop!
  // ══════════════════════════════════════════════════════════════════════════

  const loadAll = useCallback(async () => {
    if (!isAdmin || isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);

    try {
      // ── 1. Agenten laden ──
      let loadedAgenten: AgentBriefing[] = [];
      try {
        const { data, error } = await supabase
          .from('agenten_konfiguration')
          .select('*')
          .order('agent_id');

        if (!error && data && data.length > 0) {
          loadedAgenten = data.map((row: any) => ({
            id: row.agent_id,
            agent_id: row.agent_id,
            agent_name: row.name,
            agent_nummer: AGENT_NUMMERN[row.agent_id] || '99',
            beschreibung: row.beschreibung || '',
            briefing_inhalt: row.briefing || '',
            letzte_ausfuehrung: row.letzte_ausfuehrung ?? null,
            naechste_ausfuehrung: null,
            intervall: row.ausfuehrung_intervall || 'zweimal_taeglich',
            ist_aktiv: row.aktiv ?? true,
            icon: row.icon || 'Bot',
            farbe: row.farbe || '#6B7280',
            bereiche: row.bereiche || [],
            darf_emails_senden: row.darf_emails_senden ?? false,
            darf_dokumente_ablegen: row.darf_dokumente_ablegen ?? true,
            darf_aufgaben_erstellen: row.darf_aufgaben_erstellen ?? true,
            darf_zahlungen_vorschlagen: row.darf_zahlungen_vorschlagen ?? true,
            managed_agent_id: row.managed_agent_id ?? null,
          }));
          loadedAgenten.sort((a, b) => a.agent_nummer.localeCompare(b.agent_nummer));
        }
      } catch (err) {
        console.error('Agenten laden:', err);
      }

      agentenRef.current = loadedAgenten;
      setAgenten(loadedAgenten);

      // ── 2. Parallel: Vorschläge, Lernregeln, Protokoll, Mitarbeiter ──
      // Load mitarbeiter (for task approval form assignee dropdown)
      supabase
        .from('mitarbeiter')
        .select('id, vorname, nachname')
        .eq('ist_aktiv', true)
        .order('nachname')
        .then(({ data }) => {
          if (data) setMitarbeiter(data as Mitarbeiter[]);
        });

      const [vorschlaegeResult, lernregelnResult, protokollResult] = await Promise.allSettled([
        loadVorschlaegeData(),
        loadLernregelnData(loadedAgenten),
        loadProtokollData(loadedAgenten),
      ]);

      if (vorschlaegeResult.status === 'fulfilled') {
        setVorschlaege(vorschlaegeResult.value);
      }
      if (lernregelnResult.status === 'fulfilled') {
        setLernregeln(lernregelnResult.value);
      }
      if (protokollResult.status === 'fulfilled') {
        setProtokoll(protokollResult.value);
      }

    } catch (err) {
      console.error('Laden Fehler:', err);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      hasLoadedRef.current = true;
    }
  }, [isAdmin]); // NUR isAdmin als Dependency — stabil!

  // ── Einmaliger Load bei Mount ──
  useEffect(() => {
    if (!hasLoadedRef.current && isAdmin) {
      loadAll();
    }
  }, [isAdmin, loadAll]);

  // ══════════════════════════════════════════════════════════════════════════
  // DATEN-LADE-FUNKTIONEN (pure, kein setState — geben Daten zurück)
  // ══════════════════════════════════════════════════════════════════════════

  return useMemo(() => {
    // ── Berechnete Werte ──
    const pendingVorschlaege = vorschlaege.filter(v => !v.entscheidung && !v.antwort);
    const heutigeVorschlaege = vorschlaege.filter(v => v.datum === HEUTE);

    const vorschlaegeProBereich = BEREICHE.map(bereich => ({
      bereich,
      eintraege: vorschlaege.filter(v => v.bereich === bereich.id),
      pending: vorschlaege.filter(v => v.bereich === bereich.id && !v.entscheidung && !v.antwort).length,
    }));

    const protokollProAgent: Record<string, ProtokollEintrag[]> = {};
    for (const p of protokoll) {
      if (!protokollProAgent[p.agent_id]) protokollProAgent[p.agent_id] = [];
      protokollProAgent[p.agent_id].push(p);
    }

    const stats = {
      total: vorschlaege.length,
      pending: pendingVorschlaege.length,
      approved: vorschlaege.filter(v => v.entscheidung === 'ja').length,
      rejected: vorschlaege.filter(v => v.entscheidung === 'nein').length,
      improved: vorschlaege.filter(v => v.entscheidung === 'verbesserung').length,
      heute: heutigeVorschlaege.length,
      regeln: lernregeln.filter(r => r.aktiv).length,
      protokollGesamt: protokoll.length,
      protokollErfolgreich: protokoll.filter(p => p.erfolgreich).length,
      agentenAktiv: agenten.filter(a => a.ist_aktiv).length,
      agentenGesamt: agenten.length,
    };

    return {
      // Daten
      vorschlaege,
      heutigeVorschlaege,
      pendingVorschlaege,
      vorschlaegeProBereich,
      lernregeln,
      protokoll,
      protokollProAgent,
      agenten,
      mitarbeiter,
      stats,
      // Status
      loading,
      saving,
      activeTab,
      setActiveTab,
      // Aktionen (werden unten definiert)
      entscheiden: async (vorschlagId: string, entscheidung: Entscheidung, verbesserungText?: string) => {
        setSaving(vorschlagId);
        try {
          const vorschlag = vorschlaege.find(v => v.id === vorschlagId);
          const quelle = vorschlag?.meta?.quelle;

          if (quelle === 'agenten_vorschlaege') {
            const updates: Record<string, any> = {
              user_entscheidung: entscheidung,
              entschieden_am: new Date().toISOString(),
              status: entscheidung === 'ja' ? 'angenommen' :
                      entscheidung === 'nein' ? 'abgelehnt' :
                      entscheidung === 'verbesserung' ? 'verbessert' : 'offen',
            };
            if (verbesserungText) updates.user_kommentar = verbesserungText;

            const { error } = await supabase
              .from('agenten_vorschlaege')
              .update(updates)
              .eq('id', vorschlagId);

            if (error) { console.error('Entscheidung speichern:', error.message); return false; }

            await supabase.from('agenten_feedback').insert({
              agent_id: vorschlag?.meta?.agent_id || 'unknown',
              vorschlag_id: vorschlagId,
              feedback_typ: entscheidung === 'ja' ? 'angenommen' :
                            entscheidung === 'nein' ? 'abgelehnt' : 'verbessert',
              original_vorschlag: vorschlag?.vorschlag_text || vorschlag?.inhalt,
              verbesserter_text: verbesserungText || null,
            });
          } else {
            const updates: Record<string, any> = {
              entscheidung,
              gelesen: true,
              antwort_datum: new Date().toISOString(),
            };
            if (entscheidung === 'verbesserung' && verbesserungText) {
              updates.verbesserung_text = verbesserungText;
              updates.antwort = verbesserungText;
            } else if (entscheidung === 'ja') {
              updates.antwort = 'Genehmigt';
            } else if (entscheidung === 'nein') {
              updates.antwort = 'Abgelehnt';
            }

            const { error } = await supabase
              .from('ki_briefing')
              .update(updates)
              .eq('id', vorschlagId);

            if (error) { console.error('Entscheidung speichern:', error.message); return false; }
          }

          setVorschlaege(prev =>
            prev.map(v =>
              v.id === vorschlagId ? {
                ...v,
                entscheidung,
                gelesen: true,
                antwort_datum: new Date().toISOString(),
                verbesserung_text: verbesserungText || v.verbesserung_text,
                antwort: verbesserungText || (entscheidung === 'ja' ? 'Genehmigt' : entscheidung === 'nein' ? 'Abgelehnt' : v.antwort),
              } : v
            )
          );
          return true;
        } catch (err) {
          console.error('Entscheidung Fehler:', err);
          return false;
        } finally {
          setSaving(null);
        }
      },

      createLernregel: async (regel: {
        kategorie: string;
        muster: string;
        aktion: string;
        beispiel?: string;
        agent_id?: string;
      }) => {
        try {
          const { error } = await supabase
            .from('agenten_lernregeln')
            .insert({
              agent_id: regel.agent_id || null,
              regel_name: regel.muster,
              beschreibung: regel.beispiel || regel.aktion,
              wenn_bedingung: { kategorie: regel.kategorie, muster: regel.muster },
              dann_aktion: { aktion: regel.aktion, label: regel.aktion },
              gelernt_aus: 'manuell',
              aktiv: true,
              konfidenz: 0.80,
            });

          if (error) {
            console.error('Lernregel erstellen:', error.message);
            return false;
          }

          // Daten neu laden ohne Flicker
          const fresh = await loadLernregelnData(agentenRef.current);
          setLernregeln(fresh);
          return true;
        } catch (err) {
          console.error('Lernregel Fehler:', err);
          return false;
        }
      },

      toggleLernregel: async (regelId: string, aktiv: boolean) => {
        try {
          const { error } = await supabase
            .from('agenten_lernregeln')
            .update({ aktiv })
            .eq('id', regelId);

          if (error) {
            await supabase
              .from('ki_cockpit_eintraege')
              .update({ status: aktiv ? 'aktiv' : 'archiviert' })
              .eq('id', regelId);
          }

          // Optimistisches Update (kein Reload nötig)
          setLernregeln(prev =>
            prev.map(r => r.id === regelId ? { ...r, aktiv } : r)
          );
          return true;
        } catch {
          return false;
        }
      },

      toggleAgent: async (agentId: string, aktiv: boolean) => {
        try {
          const { error } = await supabase
            .from('agenten_konfiguration')
            .update({ aktiv })
            .eq('agent_id', agentId);

          if (error) {
            console.error('Agent toggle:', error.message);
            return false;
          }

          // Optimistisches Update
          setAgenten(prev =>
            prev.map(a => a.agent_id === agentId ? { ...a, ist_aktiv: aktiv } : a)
          );
          agentenRef.current = agentenRef.current.map(a =>
            a.agent_id === agentId ? { ...a, ist_aktiv: aktiv } : a
          );
          return true;
        } catch {
          return false;
        }
      },

      markAlleGelesen: async () => {
        const ungelesene = vorschlaege.filter(v => !v.gelesen);
        if (ungelesene.length === 0) return;

        const neueIds = ungelesene.filter(v => v.meta?.quelle === 'agenten_vorschlaege').map(v => v.id);
        if (neueIds.length > 0) {
          await supabase
            .from('agenten_vorschlaege')
            .update({ status: 'gelesen' })
            .in('id', neueIds);
        }

        const legacyIds = ungelesene.filter(v => v.meta?.quelle !== 'agenten_vorschlaege').map(v => v.id);
        if (legacyIds.length > 0) {
          await supabase
            .from('ki_briefing')
            .update({ gelesen: true })
            .in('id', legacyIds);
        }

        setVorschlaege(prev => prev.map(v => ({ ...v, gelesen: true })));
      },

      createProtokollEintrag: async (eintrag: {
        agent_id: string;
        aktion: string;
        beschreibung: string;
        details?: Record<string, any>;
        erfolgreich?: boolean;
        fehler_meldung?: string;
      }) => {
        try {
          const { error } = await supabase
            .from('agenten_protokoll')
            .insert({
              agent_id: eintrag.agent_id,
              aktion: eintrag.aktion,
              beschreibung: eintrag.beschreibung,
              details: eintrag.details || {},
              erfolgreich: eintrag.erfolgreich ?? true,
              fehler_meldung: eintrag.fehler_meldung || null,
            });

          if (error) {
            console.error('Protokoll erstellen:', error.message);
            return false;
          }

          // Daten neu laden ohne Flicker
          const fresh = await loadProtokollData(agentenRef.current);
          setProtokoll(fresh);
          return true;
        } catch {
          return false;
        }
      },

      vorschlagAktion: async (vorschlagId: string, aktion: string, details: Record<string, any>) => {
        setSaving(vorschlagId);
        try {
          const vorschlag = vorschlaege.find(v => v.id === vorschlagId);
          const quelle = vorschlag?.meta?.quelle;

          // 1. Vorschlag in Supabase aktualisieren
          if (quelle === 'agenten_vorschlaege') {
            const updates: Record<string, any> = {
              aktions_typ: aktion,
              user_anweisung: details.anweisung || null,
              ziel_agent_id: details.ziel_agent || null,
              ausfuehrung_status: details.geplante_zeit ? 'geplant' : 'in_bearbeitung',
              vorgeschlagene_aktionen: JSON.stringify([
                { typ: aktion, label: aktion, text: details.anweisung, zeit: details.geplante_zeit },
              ]),
            };
            if (details.geplante_zeit) {
              const heute = new Date();
              const [h, m] = details.geplante_zeit.split(':');
              heute.setHours(parseInt(h), parseInt(m), 0, 0);
              updates.geplante_ausfuehrung = heute.toISOString();
            }
            if (aktion !== 'anweisung') {
              updates.user_entscheidung = 'ja';
              updates.entschieden_am = new Date().toISOString();
              updates.status = 'angenommen';
            }

            await supabase
              .from('agenten_vorschlaege')
              .update(updates)
              .eq('id', vorschlagId);
          } else {
            // Legacy ki_briefing
            const updates: Record<string, any> = {
              entscheidung: aktion === 'anweisung' ? null : 'ja',
              gelesen: true,
              antwort_datum: new Date().toISOString(),
              antwort: details.anweisung || `Aktion: ${aktion}`,
            };
            await supabase
              .from('ki_briefing')
              .update(updates)
              .eq('id', vorschlagId);
          }

          // 2. Feedback-Eintrag erstellen (für Lernschleife)
          await supabase.from('agenten_feedback').insert({
            agent_id: vorschlag?.meta?.agent_id || details.ziel_agent || 'system',
            vorschlag_id: vorschlagId,
            feedback_typ: aktion === 'anweisung' ? 'anweisung' : 'aktion',
            original_vorschlag: vorschlag?.vorschlag_text || vorschlag?.inhalt,
            verbesserter_text: details.anweisung || null,
            user_anweisung: details.anweisung || null,
            ziel_agent_id: details.ziel_agent || null,
            gewaehlte_aktion: aktion,
            geplante_zeit: details.geplante_zeit ? (() => {
              const d = new Date();
              const [h, m] = details.geplante_zeit.split(':');
              d.setHours(parseInt(h), parseInt(m), 0, 0);
              return d.toISOString();
            })() : null,
          });

          // 3. Protokoll-Eintrag erstellen
          await supabase.from('agenten_protokoll').insert({
            agent_id: details.ziel_agent || vorschlag?.meta?.agent_id || 'system',
            aktion: `vorschlag_${aktion}`,
            beschreibung: `Aktion "${aktion}" für Vorschlag "${vorschlag?.titel}"${details.anweisung ? ` — Anweisung: ${details.anweisung}` : ''}`,
            details: {
              vorschlag_id: vorschlagId,
              aktion,
              ...details,
            },
            erfolgreich: true,
          });

          // 4. Optimistisches UI-Update (nur bei echten Aktionen, nicht bei Anweisungen)
          if (aktion !== 'anweisung') {
            setVorschlaege(prev =>
              prev.map(v =>
                v.id === vorschlagId ? {
                  ...v,
                  entscheidung: 'ja' as Entscheidung,
                  gelesen: true,
                  antwort_datum: new Date().toISOString(),
                  antwort: details.anweisung || `Aktion: ${aktion}`,
                } : v
              )
            );
          }

          return true;
        } catch (err) {
          console.error('Vorschlag-Aktion Fehler:', err);
          return false;
        } finally {
          setSaving(null);
        }
      },

      aufgabeErstellen: async (vorschlagId: string, details: AufgabenDetails): Promise<boolean> => {
        setSaving(vorschlagId);
        try {
          const { error: aufgabeError } = await supabase
            .from('aufgaben')
            .insert({
              titel: details.titel,
              beschreibung: details.beschreibung || null,
              prioritaet: details.prioritaet,
              status: 'offen',
              faellig_am: details.faellig_am || null,
              zugewiesen_an: details.zugewiesen_an || '11111111-1111-1111-1111-111111111111',
              akte_id: details.akte_id || null,
              business_unit: details.business_unit,
            });

          if (aufgabeError) throw aufgabeError;

          await supabase
            .from('agenten_vorschlaege')
            .update({
              status: 'erledigt',
              user_entscheidung: 'ja',
              entschieden_am: new Date().toISOString(),
            })
            .eq('id', vorschlagId);

          setVorschlaege(prev =>
            prev.map(v =>
              v.id === vorschlagId
                ? { ...v, entscheidung: 'ja' as Entscheidung, gelesen: true, antwort_datum: new Date().toISOString() }
                : v
            )
          );
          return true;
        } catch (err) {
          console.error('Aufgabe erstellen Fehler:', err);
          return false;
        } finally {
          setSaving(null);
        }
      },

      issueInRoadmap: async (vorschlagId: string, details: IssueDetails): Promise<string | null> => {
        setSaving(vorschlagId);
        try {
          const vorschlag = vorschlaege.find(v => v.id === vorschlagId);
          const agentName = vorschlag?.erstellt_von || 'Agent';

          const bodyParts: string[] = [];
          if (details.kurzbeschreibung) {
            bodyParts.push(`> ${details.kurzbeschreibung}`, '');
          }
          bodyParts.push(
            details.beschreibung,
            '',
            '---',
            `_Vorgeschlagen von Agent: ${agentName}_`,
          );
          if (details.fehler_log) {
            bodyParts.push('', '**Fehler-Log:**', '```', details.fehler_log, '```');
          }
          const body = bodyParts.join('\n');

          const labels = computeGitHubLabels(details);

          const targetRepo = (vorschlag?.meta?.github_repo as string | undefined) || 'AndKo88/gutachter-koop-app';
          const { data: { session: _ghSession } } = await supabase.auth.getSession();
          const response = await fetch('/api/github-sync', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(_ghSession?.access_token ? { Authorization: `Bearer ${_ghSession.access_token}` } : {}),
            },
            body: JSON.stringify({
              action: 'create_issue',
              repo: targetRepo,
              title: details.titel,
              body,
              labels,
            }),
          });

          if (!response.ok) {
            const err = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error((err as { error: string }).error || 'GitHub API Fehler');
          }

          const { issue } = await response.json() as { issue: { number: number; html_url: string } };

          await supabase
            .from('agenten_vorschlaege')
            .update({
              status: 'erledigt',
              user_entscheidung: 'ja',
              entschieden_am: new Date().toISOString(),
            })
            .eq('id', vorschlagId);

          setVorschlaege(prev =>
            prev.map(v =>
              v.id === vorschlagId
                ? { ...v, entscheidung: 'ja' as Entscheidung, gelesen: true, antwort_datum: new Date().toISOString() }
                : v
            )
          );

          return issue.html_url;
        } catch (err) {
          console.error('Issue in Roadmap Fehler:', err);
          return null;
        } finally {
          setSaving(null);
        }
      },

      // ═══ NEU: Manueller Vorschlag erstellen (Eingabefeld in Kommandozentrale) ═══
      createManualVorschlag: async (params: {
        titel: string;
        inhalt: string;
        zielAgent?: string;
        kategorie?: string;
        prioritaet?: number;
        typ?: string;
      }) => {
        try {
          const { error } = await supabase
            .from('agenten_vorschlaege')
            .insert({
              agent_id: params.zielAgent || 'tagesmanager',
              agent_name: params.zielAgent
                ? (agenten.find(a => a.agent_id === params.zielAgent)?.agent_name || params.zielAgent)
                : 'Tagesmanager',
              titel: params.titel,
              beschreibung: params.inhalt,
              vorschlag_text: params.inhalt,
              kategorie: params.kategorie || 'allgemein',
              vorschlag_typ: params.typ || 'manuell',
              prioritaet: params.prioritaet || 7,
              status: 'offen',
              kontext: {
                erstellt_von: params.typ === 'vorschlag' ? 'vorschlag' : 'benutzer',
                typ: params.typ === 'vorschlag' ? 'vorschlag' : 'manuelle_eingabe',
                ziel_agent: params.zielAgent || 'alle',
              },
            });

          if (error) {
            console.error('Manueller Vorschlag Fehler:', error.message);
            return false;
          }

          // Feedback-Eintrag für Lernschleife
          await supabase.from('agenten_feedback').insert({
            agent_id: params.zielAgent || 'tagesmanager',
            feedback_typ: 'anweisung',
            user_anweisung: `${params.titel}: ${params.inhalt}`,
            ziel_agent_id: params.zielAgent || null,
          });

          // Protokoll
          await supabase.from('agenten_protokoll').insert({
            agent_id: 'system',
            aktion: 'manueller_vorschlag',
            beschreibung: `Neuer Vorschlag: ${params.titel}`,
            details: {
              titel: params.titel,
              inhalt: params.inhalt,
              ziel_agent: params.zielAgent,
              kategorie: params.kategorie,
            },
            erfolgreich: true,
          });

          // Daten neu laden
          const fresh = await loadVorschlaegeData();
          setVorschlaege(fresh);
          const freshProto = await loadProtokollData(agentenRef.current);
          setProtokoll(freshProto);
          return true;
        } catch (err) {
          console.error('Manueller Vorschlag Fehler:', err);
          return false;
        }
      },

      // ═══ NEU: E-Mail für Agent-System verarbeiten ═══
      processEmailForAgent: async (email: {
        id: string;
        threadId: string;
        subject: string;
        from: string;
        fromName: string;
        body: string;
        snippet: string;
        date: string;
        attachments?: Array<{ filename: string; mimeType: string; size: number; attachmentId: string }>;
      }) => {
        try {
          const { data: { session: emailSession } } = await supabase.auth.getSession();
          const response = await fetch('/api/agenten-email-processor', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(emailSession?.access_token ? { 'Authorization': `Bearer ${emailSession.access_token}` } : {}),
            },
            body: JSON.stringify({ action: 'processEmail', email }),
          });
          const result = await response.json();

          if (result.success && result.results?.length > 0) {
            // Vorschläge neu laden
            const fresh = await loadVorschlaegeData();
            setVorschlaege(fresh);
          }

          return result;
        } catch (err) {
          console.error('E-Mail Agent Fehler:', err);
          return { success: false, error: 'Verarbeitungsfehler' };
        }
      },

      // ═══ NEU: Stellungnahme aus Vorschlag vorbereiten ═══
      prepareStellungnahme: async (vorschlagId: string) => {
        try {
          const { data: { session: stellungSession } } = await supabase.auth.getSession();
          const response = await fetch('/api/agenten-email-processor', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(stellungSession?.access_token ? { 'Authorization': `Bearer ${stellungSession.access_token}` } : {}),
            },
            body: JSON.stringify({ action: 'prepareStellungnahme', vorschlagId }),
          });
          const result = await response.json();

          if (result.success) {
            // Vorschläge und Protokoll neu laden
            const fresh = await loadVorschlaegeData();
            setVorschlaege(fresh);
            const freshProto = await loadProtokollData(agentenRef.current);
            setProtokoll(freshProto);
          }

          return result;
        } catch (err) {
          console.error('Stellungnahme Fehler:', err);
          return { success: false, error: 'Verarbeitungsfehler' };
        }
      },

      reload: loadAll,
    };
  }, [vorschlaege, lernregeln, protokoll, agenten, mitarbeiter, loading, saving, activeTab, loadAll]);
}


// ══════════════════════════════════════════════════════════════════════════════
// PURE DATA LOADERS (außerhalb des Hooks — kein setState, kein Re-Render)
// ══════════════════════════════════════════════════════════════════════════════

async function loadVorschlaegeData(): Promise<AgentenVorschlag[]> {
  const siebenTageZurueck = new Date();
  siebenTageZurueck.setDate(siebenTageZurueck.getDate() - 30);
  const vonDatum = siebenTageZurueck.toISOString();

  const allVorschlaege: AgentenVorschlag[] = [];

  try {
    // Primär: agenten_vorschlaege
    const { data: neueVorschlaege, error: neueError } = await supabase
      .from('agenten_vorschlaege')
      .select('*')
      .gte('erstellt_am', vonDatum)
      .order('erstellt_am', { ascending: false })
      .limit(100);

    if (!neueError && neueVorschlaege) {
      for (const row of neueVorschlaege) {
        allVorschlaege.push({
          id: row.id,
          datum: row.erstellt_am?.split('T')[0] || HEUTE,
          bereich: row.kategorie || 'allgemein',
          typ: 'vorschlag',
          vorschlag_typ: (row.vorschlag_typ as VorschlagTyp) || 'allgemein',
          titel: row.titel,
          inhalt: row.beschreibung || '',
          vorschlag_text: row.vorschlag_text || null,
          entscheidung: row.user_entscheidung === 'ja' ? 'ja' :
                        row.user_entscheidung === 'nein' ? 'nein' :
                        row.user_entscheidung === 'verbesserung' ? 'verbesserung' : null,
          verbesserung_text: row.user_kommentar || null,
          antwort: row.user_kommentar || null,
          antwort_datum: row.entschieden_am || null,
          gelesen: row.status !== 'offen',
          erstellt_am: row.erstellt_am,
          erstellt_von: row.agent_name || row.agent_id || 'ki_agent',
          meta: {
            ...(row.kontext || {}),
            quelle: 'agenten_vorschlaege',
            agent_id: row.agent_id,
            agent_name: row.agent_name,
            prioritaet: row.prioritaet,
            status: row.status,
          },
          aufgaben_details: (row.aufgaben_details as AufgabenDetails) || null,
          issue_details: (row.issue_details as IssueDetails) || null,
        });
      }
    }

    // Sekundär: ki_briefing (Legacy)
    const { data: legacyVorschlaege, error: legacyError } = await supabase
      .from('ki_briefing')
      .select('*')
      .gte('datum', siebenTageZurueck.toISOString().split('T')[0])
      .order('erstellt_am', { ascending: false })
      .limit(50);

    if (!legacyError && legacyVorschlaege) {
      for (const row of legacyVorschlaege) {
        const exists = allVorschlaege.some(v => v.titel === row.titel);
        if (exists) continue;

        allVorschlaege.push({
          id: row.id,
          datum: row.datum,
          bereich: row.bereich,
          typ: row.typ || 'meldung',
          vorschlag_typ: row.vorschlag_typ || null,
          titel: row.titel,
          inhalt: row.inhalt,
          vorschlag_text: row.vorschlag_text || null,
          entscheidung: row.entscheidung || null,
          verbesserung_text: row.verbesserung_text || null,
          antwort: row.antwort || null,
          antwort_datum: row.antwort_datum || null,
          gelesen: row.gelesen || false,
          erstellt_am: row.erstellt_am,
          erstellt_von: row.erstellt_von || 'ki_agent',
          meta: { ...(row.meta || {}), quelle: 'ki_briefing' },
          aufgaben_details: null,
          issue_details: null,
        });
      }
    }
  } catch (err) {
    console.error('Vorschläge laden:', err);
  }

  allVorschlaege.sort((a, b) => new Date(b.erstellt_am).getTime() - new Date(a.erstellt_am).getTime());
  return allVorschlaege;
}


async function loadLernregelnData(agenten: AgentBriefing[]): Promise<LernRegel[]> {
  try {
    const { data, error } = await supabase
      .from('agenten_lernregeln')
      .select('*')
      .order('erstellt_am', { ascending: false });

    if (error) {
      console.error('Lernregeln laden:', error.message);
      // Fallback: ki_cockpit_eintraege
      const { data: legacy, error: legacyErr } = await supabase
        .from('ki_cockpit_eintraege')
        .select('*')
        .eq('typ', 'lernregel')
        .eq('status', 'aktiv')
        .order('erstellt_am', { ascending: false });

      if (!legacyErr && legacy && legacy.length > 0) {
        return legacy.map((row: any) => ({
          id: row.id,
          kategorie: row.meta?.kategorie || 'Allgemein',
          muster: row.titel,
          aktion: row.inhalt || '',
          beispiel: row.meta?.beispiel || null,
          aktiv: row.status === 'aktiv',
          anwendungen: row.meta?.anwendungen || 0,
          erstellt_am: row.erstellt_am,
          erstellt_von: row.erstellt_von,
        }));
      }
      return [];
    }

    if (data && data.length > 0) {
      return data.map((row: any) => {
        const dann = row.dann_aktion || {};
        const agentInfo = row.agent_id ? agenten.find(a => a.agent_id === row.agent_id) : null;
        return {
          id: row.id,
          kategorie: agentInfo?.agent_name || row.agent_id || 'Alle Agenten',
          muster: row.regel_name || row.beschreibung || '',
          aktion: dann.label || dann.aktion || row.beschreibung || JSON.stringify(dann),
          beispiel: row.beschreibung || null,
          aktiv: row.aktiv ?? true,
          anwendungen: row.anwendungen || 0,
          erstellt_am: row.erstellt_am,
          erstellt_von: row.gelernt_aus === 'manuell' ? 'admin' as const :
                        row.gelernt_aus === 'muster_erkannt' ? 'ki_agent' as const : 'system' as const,
          agent_id: row.agent_id,
          konfidenz: row.konfidenz ? parseFloat(row.konfidenz) : undefined,
        };
      });
    }
    return [];
  } catch (err) {
    console.error('Lernregeln Fehler:', err);
    return [];
  }
}


// ── Feedback-History für einen Vorschlag laden ──
export async function loadFeedbackForVorschlag(vorschlagId: string): Promise<FeedbackEintrag[]> {
  try {
    const { data, error } = await supabase
      .from('agenten_feedback')
      .select('*')
      .eq('vorschlag_id', vorschlagId)
      .order('erstellt_am', { ascending: true });

    if (error || !data) return [];

    return data.map((row: any) => ({
      id: row.id,
      agent_id: row.agent_id,
      vorschlag_id: row.vorschlag_id,
      feedback_typ: row.feedback_typ,
      original_vorschlag: row.original_vorschlag,
      verbesserter_text: row.verbesserter_text,
      user_anweisung: row.user_anweisung,
      ziel_agent_id: row.ziel_agent_id,
      gewaehlte_aktion: row.gewaehlte_aktion,
      geplante_zeit: row.geplante_zeit,
      erstellt_am: row.erstellt_am,
    }));
  } catch (err) {
    console.error('Feedback laden:', err);
    return [];
  }
}

// ── Kontext (Feedback + Protokoll) für einen Vorschlag laden ──
export async function loadKontextForVorschlag(vorschlagId: string): Promise<KontextEintrag[]> {
  try {
    const [feedbackResult, protokollResult] = await Promise.allSettled([
      supabase
        .from('agenten_feedback')
        .select('*')
        .eq('vorschlag_id', vorschlagId)
        .order('erstellt_am', { ascending: true }),
      supabase
        .from('agenten_protokoll')
        .select('*')
        .order('erstellt_am', { ascending: false })
        .limit(200),
    ]);

    const kontext: KontextEintrag[] = [];

    // Feedback-Einträge
    if (feedbackResult.status === 'fulfilled' && feedbackResult.value.data) {
      for (const row of feedbackResult.value.data) {
        const typLabel = row.feedback_typ === 'angenommen' ? 'Genehmigt' :
                         row.feedback_typ === 'abgelehnt' ? 'Abgelehnt' :
                         row.feedback_typ === 'verbessert' ? 'Verbesserung angefordert' :
                         row.feedback_typ === 'aktion' ? `Aktion: ${row.gewaehlte_aktion || 'unbekannt'}` :
                         row.feedback_typ === 'anweisung' ? 'Anweisung erteilt' :
                         row.feedback_typ;

        kontext.push({
          id: row.id,
          typ: 'feedback',
          titel: typLabel,
          beschreibung: row.user_anweisung || row.verbesserter_text || row.original_vorschlag || '',
          datum: row.erstellt_am,
          icon_typ: row.feedback_typ,
          details: {
            gewaehlte_aktion: row.gewaehlte_aktion,
            ziel_agent_id: row.ziel_agent_id,
            geplante_zeit: row.geplante_zeit,
          },
        });
      }
    }

    // Protokoll-Einträge die zu diesem Vorschlag gehören
    if (protokollResult.status === 'fulfilled' && protokollResult.value.data) {
      for (const row of protokollResult.value.data) {
        if (row.details?.vorschlag_id === vorschlagId) {
          kontext.push({
            id: row.id,
            typ: 'protokoll',
            titel: row.aktion?.replace(/_/g, ' ') || 'Aktion',
            beschreibung: row.beschreibung || '',
            datum: row.erstellt_am,
            icon_typ: row.aktion || 'system',
            details: row.details,
          });
        }
      }
    }

    // Chronologisch sortieren (älteste zuerst)
    kontext.sort((a, b) => new Date(a.datum).getTime() - new Date(b.datum).getTime());
    return kontext;
  } catch (err) {
    console.error('Kontext laden:', err);
    return [];
  }
}


async function loadProtokollData(agenten: AgentBriefing[]): Promise<ProtokollEintrag[]> {
  try {
    const { data, error } = await supabase
      .from('agenten_protokoll')
      .select('*')
      .order('erstellt_am', { ascending: false })
      .limit(100);

    if (error) {
      console.error('Protokoll laden:', error.message);
      return [];
    }

    if (data && data.length > 0) {
      return data.map((row: any) => {
        const agent = agenten.find(a => a.agent_id === row.agent_id);
        return {
          id: row.id,
          agent_id: row.agent_id,
          agent_name: agent?.agent_name || row.agent_id,
          aktion: row.aktion,
          beschreibung: row.beschreibung || '',
          kategorie: row.details?.kategorie || row.aktion?.split('_')[0] || 'Allgemein',
          details: row.details,
          erfolgreich: row.erfolgreich ?? true,
          fehler_meldung: row.fehler_meldung,
          erstellt_am: row.erstellt_am,
        };
      });
    }
    return [];
  } catch (err) {
    console.error('Protokoll Fehler:', err);
    return [];
  }
}
